import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, Share2 } from 'lucide-react';
import { libraryError, libraryFileUrl, libraryHeaders, librarySessionExpired } from '../../lib/libraryFileAccess';

export const LibraryDownloadControls = ({ file }) => {
  const [state, setState] = useState({ status: 'idle', url: null, file: null, error: '' });
  const request = useRef(null);
  const objectUrl = useRef(null);
  const lock = useRef(false);
  useEffect(() => () => {
    request.current?.abort();
    const url = objectUrl.current;
    if (url) setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, []);
  const prepare = async () => {
    if (lock.current) return;
    lock.current = true;
    const controller = new AbortController(); request.current = controller;
    setState({ status: 'loading', url: null, file: null, error: '' });
    try {
      const response = await axios.get(libraryFileUrl(file.id, 'download'), {
        headers: libraryHeaders(), responseType: 'blob', signal: controller.signal, timeout: 90000,
      });
      if (controller.signal.aborted) return;
      const prepared = new File([response.data], file.filename || 'download', { type: response.headers['content-type'] || file.mime_type || 'application/octet-stream' });
      objectUrl.current = URL.createObjectURL(prepared);
      setState({ status: 'ready', url: objectUrl.current, file: prepared, error: '' });
    } catch (error) {
      if (!controller.signal.aborted) setState({ status: librarySessionExpired(error) ? 'expired' : 'error', url: null, file: null, error: await libraryError(error) });
    } finally { lock.current = false; }
  };
  const share = async () => {
    try { await navigator.share({ files: [state.file], title: file.filename }); }
    catch (error) { if (error.name !== 'AbortError') setState((old) => ({ ...old, error: 'Could not open the sharing menu. Use Save file instead.' })); }
  };
  let shareable = false;
  try { shareable = Boolean(state.file && navigator.canShare?.({ files: [state.file] })); } catch (_) { /* device capability */ }
  const style = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-gray-50 disabled:opacity-50';
  return <div data-testid="preview-download-controls" className="flex min-w-0 flex-wrap items-center gap-2">
    {state.status === 'ready' ? <>
      <span role="status" className="text-xs text-emerald-800" data-testid="preview-download-ready">Ready to save</span>
      <a href={state.url} download={file.filename || 'download'} className={style} data-testid="preview-save-file"><Download className="h-4 w-4" />Save file</a>
      {shareable && <button type="button" onClick={share} className={style} data-testid="preview-share-file"><Share2 className="h-4 w-4" />Share file</button>}
    </> : <button type="button" onClick={prepare} disabled={state.status === 'loading'} className={style} data-testid="preview-download">
      {state.status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {state.status === 'loading' ? 'Preparing…' : 'Download original'}
    </button>}
    {state.error && <div className="w-full break-words text-sm text-red-700" role="alert" data-testid="preview-download-error">{state.error}
      {state.status === 'expired' && <a href="/admin/login" className="ml-2 underline" data-testid="preview-download-sign-in">Sign in</a>}
    </div>}
  </div>;
};